import React from "react";
import { useState, useContext, useEffect } from "react";
import { Box, Tooltip, Typography, Snackbar } from '@mui/material';
import { Button } from "@mui/material";
import { DataGrid, GridActionsCellItem, GridToolbarContainer } from "@mui/x-data-grid";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ViewStatementDialog from "./dialog-view-statement-pdf";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { InfoOutlined } from "@mui/icons-material";
import CancelIcon from '@mui/icons-material/Cancel';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useGetDocumentsForCurrentCase, useGetDocumentsStatusReport, useCancelDocument } from "../../api/document";
import DocumentGridSkeleton from "./DocumentGridSkeleton";
import ContentDialog from "./ContentDialog";
import UserProfileContext from "../../context/userprofilecontext"
import { useGetDocumentQualityReport } from "../../api/statementqualityreport";
import { useIsSuperAdmin } from "../../api/swrcurrentusercontext.js";
import { reissueDocument } from "../../api/ocrolus_scan.js";
import { useConfirmAction } from "./confirmactionhandler";
import ScanQualityReport from "./ScanQualityReport";
import ScanQualityReportSkeleton from "./ScanQualityReportSkeleton";
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

function preventDefault(event) {
  event.preventDefault();
}

function enableDefault(event) {
  return;
}

function DocumentQualityDialog({ open, onClose, selectedDocument }) {
  const documentId = selectedDocument?.documentID;
  const { data: documentQualityReport, error, isLoading } = useGetDocumentQualityReport(
    documentId,
    {},
    open
  );

  if (!selectedDocument) {
    return null;
  }

  return (
    <ContentDialog
      open={open}
      onClose={onClose}
      fullScreen={false}
      hideFooter={true}
      title={`Document Quality Details for ${selectedDocument.documentName}`}
      maxWidth="lg"
      fullWidth={true}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          height: 'auto',
        },
      }}
      contentProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
          flex: 1,
          minHeight: 0,
        },
      }}
    >
      {isLoading ? (
        <Box sx={{ overflow: 'auto', flex: 1, p: 3 }}>
          <ScanQualityReportSkeleton extractedDocCount={2} />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            An error occurred while loading the document quality report: {error.message}
          </Alert>
        </Box>
      ) : (
        <Box sx={{ overflow: 'auto', flex: 1, p: 3 }}>
          <ScanQualityReport data={documentQualityReport} showDetailedChecks={false} />
        </Box>
      )}
    </ContentDialog>
  );
}

function CancelScanConfirmDialog({ open, onConfirm, onClose, document }) {
  if (!document) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel Scan</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to cancel the scan for the following document?
        </DialogContentText>
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2"><strong>Document Name:</strong> {document.documentName}</Typography>
          <Typography variant="body2"><strong>Status:</strong> {document.displayStatus}</Typography>
          {document.numPages && (
            <Typography variant="body2"><strong>Pages:</strong> {document.numPages}</Typography>
          )}
          {document.created && (
            <Typography variant="body2"><strong>Uploaded:</strong> {new Date(document.created).toLocaleString()}</Typography>
          )}
          {document.bank_account_name && (
            <Typography variant="body2"><strong>Bank Account:</strong> {document.bank_account_name}</Typography>
          )}
        </Box>
        <DialogContentText sx={{ mt: 2, color: 'error.main' }}>
          This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">No, Keep It</Button>
        <Button onClick={onConfirm} color="error" variant="contained">
          Yes, Cancel Scan
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DocumentSummaryDialog({ open, onClose, rows }) {
  const summary = rows.reduce((acc, row) => {
    const status = row.displayStatus || 'Unknown';
    if (!acc[status]) acc[status] = { count: 0, pages: 0 };
    acc[status].count += 1;
    acc[status].pages += row.numPages || 0;
    return acc;
  }, {});

  const totals = Object.values(summary).reduce(
    (acc, { count, pages }) => ({ count: acc.count + count, pages: acc.pages + pages }),
    { count: 0, pages: 0 }
  );

  const colStatus = { flex: 1 };
  const colCount  = { width: 100, textAlign: 'right' };
  const colPages  = { width: 100, textAlign: 'right' };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Document Summary</DialogTitle>
      <DialogContent>
        {/* Header row */}
        <Box sx={{ display: 'flex', pb: 1, borderBottom: '2px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ ...colStatus, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Status
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ...colCount, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Documents
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ...colPages, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Pages
          </Typography>
        </Box>
        {/* Data rows */}
        {Object.entries(summary).map(([status, { count, pages }]) => (
          <Box
            key={status}
            sx={{ display: 'flex', alignItems: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Typography variant="body2" sx={{ ...colStatus }}>{status}</Typography>
            <Typography variant="body2" sx={{ ...colCount }}>{count}</Typography>
            <Typography variant="body2" sx={{ ...colPages }}>{pages}</Typography>
          </Box>
        ))}
        {/* Totals row */}
        <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
          <Typography variant="body2" sx={{ ...colStatus, fontWeight: 600 }}>Total</Typography>
          <Typography variant="body2" sx={{ ...colCount, fontWeight: 600 }}>{totals.count}</Typography>
          <Typography variant="body2" sx={{ ...colPages, fontWeight: 600 }}>{totals.pages}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

const cancelableStatuses = ['PAGE_QUOTA_EXCEEDED', 'UPLOAD_COMPLETE', 'PAGE_COUNT_COMPLETE', 'PAGE_COUNT_ERROR'];

export default function DocumentPreview(props) {
  const handleUploadClick = props.handleUploadClick ? props.handleUploadClick : () => { console.log('DEFAULT UPLOAD HANDLER') }
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDocumentQualityDialog, setOpenDocumentQualityDialog] = useState(false);
  const [openCancelConfirmDialog, setOpenCancelConfirmDialog] = useState(false);
  const [openSummaryDialog, setOpenSummaryDialog] = useState(false);
  const [documentToView, setDocumentToView] = useState();
  const [documentToCancel, setDocumentToCancel] = useState(null);
  const previewResult = useGetDocumentsForCurrentCase()
  const documentStatusResult = useGetDocumentsStatusReport()
  const cancelDocumentById = useCancelDocument()

  const { userProfile, isLoading: userProfileLoading } = useContext(UserProfileContext);
  const isSuperAdminResult = useIsSuperAdmin();
  const [reissueSuccess, setReissueSuccess] = useState(false);
  const [reissueError, setReissueError] = useState(null);

  const handleReissueDocument = async (document) => {
    try {
      await reissueDocument(document.documentID);
      setReissueSuccess(true);
      previewResult.mutate?.();
      documentStatusResult.mutate?.();
    } catch (e) {
      console.error('Re-issue failed', e);
      setReissueError(e?.message || 'Re-issue failed. Please try again.');
    }
  };

  const { handleConfirmAction: confirmReissue, ConfirmDialogElement: ReissueConfirmDialog } = useConfirmAction(
    handleReissueDocument,
    {
      title: 'Re-issue document scan',
      entityNamePrefix: 'You are about to re-issue the scan for:',
      getEntityName: (doc) => doc?.documentName,
      confirmMessage: 'This invalidates the existing published statement(s) for this document and runs a fresh scan on the current pipeline. The existing results are kept (tombstoned) for audit — not deleted.',
      proceedButtonLabel: 'Re-issue',
      destructive: true,
    }
  );

  if (previewResult.isLoading
    || !previewResult.data
    || documentStatusResult.isLoading
    || !documentStatusResult.data
    || userProfileLoading) {
    return <DocumentGridSkeleton />
  }

  if (previewResult.error) {
    return <div>Error: {previewResult.error.message}</div>
  }

  const rows = previewResult.data.map(
    (row, index) => ({ 'id': index, ...row })
  );

  const StatusCellWithTooltip = (params) => {
    const tooltipText = params.row.ocrolus_notification_reason || '';

    return (
      <Tooltip
        title={tooltipText}
        placement="top"
        arrow
        disableHoverListener={!tooltipText}
      >
        <span>
          {params.value}
        </span>
      </Tooltip>
    );
  };

  const action_column = {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 170,
    cellClassName: 'actions',
    getActions: ({ id }) => {
      const row = rows.find(r => r.id === id);
      const isCancelable = cancelableStatuses.includes(row?.status);
      // Super-admin re-issue: only on documents that aren't mid-scan
      // (terminal state), so it doesn't collide with an in-flight scan.
      const canReissue = Boolean(isSuperAdminResult?.data) && !isCancelable;

      const actions = [
        <GridActionsCellItem
          icon={<PictureAsPdfIcon />}
          label="View PDF"
          className="textPrimary"
          onClick={handlePDFClick(id)}
          color="inherit"
        />,
        <GridActionsCellItem
          icon={<InfoOutlined />}
          label="View Document Quality"
          className="textPrimary"
          onClick={handleQualityClick(id)}
          color="inherit"
        />,
        <GridActionsCellItem
          icon={<CancelIcon />}
          label="Cancel Scan"
          className="textPrimary"
          onClick={handleCancelDocumentClick(id)}
          color="inherit"
          disabled={!isCancelable}
        />
      ];

      if (canReissue) {
        actions.push(
          <GridActionsCellItem
            icon={<RestartAltIcon sx={{ color: 'error.main' }} />}
            label="Re-issue scan"
            className="textPrimary"
            onClick={() => confirmReissue(row)}
            color="error"
          />
        );
      }

      return actions;
    },
  }

  const keys = [
    { field: 'documentName', headerName: 'Document Name', width: 300 },
    { field: 'displayStatus', headerName: 'Status', renderCell: StatusCellWithTooltip, width: 130 },
    { field: 'numPages', headerName: 'Page Count', width: 120 },
    { field: 'created', headerName: 'Uploaded Date', width: 280 },
    { field: 'bank_account_name', headerName: 'Bank Account', width: 400 },
    { field: 'statement_begin_date', headerName: 'Begin Date', width: 140 },
    { field: 'statement_end_date', headerName: 'End Date', width: 140 },
  ]

  let columns = [action_column, ...keys]

  function EditToolbar(props) {
    return (
      <GridToolbarContainer sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button color="primary" startIcon={<FileUploadIcon />} onClick={handleUploadClick}>
          Click Here to Upload New Files
        </Button>
        <Button
          size="small"
          color="inherit"
          sx={{ textTransform: 'none', fontSize: '0.8rem', color: 'text.secondary' }}
          onClick={() => setOpenSummaryDialog(true)}
        >
          {rows.length} document{rows.length !== 1 ? 's' : ''} — view summary
        </Button>
      </GridToolbarContainer>
    );
  }

  const handlePDFClick = (id) => () => {
    const selectedDocument = rows.find(row => row.id === id);
    setDocumentToView(selectedDocument);
    setOpenViewDialog(true);
  }

  const handleCancelDocumentClick = (id) => () => {
    const selectedDocument = rows.find(row => row.id === id);
    setDocumentToCancel(selectedDocument);
    setOpenCancelConfirmDialog(true);
  }

  const handleConfirmCancel = async () => {
    if (documentToCancel) {
      await cancelDocumentById(documentToCancel.documentID);
    }
    setOpenCancelConfirmDialog(false);
    setDocumentToCancel(null);
  };

  const handleCloseCancelConfirmDialog = () => {
    setOpenCancelConfirmDialog(false);
    setDocumentToCancel(null);
  };

  function onCancelView() {
    setOpenViewDialog(false);
  };

  const handleQualityClick = (id) => () => {
    const selectedDocument = rows.find(row => row.id === id);
    setDocumentToView(selectedDocument);
    setOpenDocumentQualityDialog(true);
  }

  const onCancelDocumentQualityDialog = () => {
    setOpenDocumentQualityDialog(false);
  };

  return (
    <React.Fragment>
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', maxWidth: '1800px', margin: '0 auto', minHeight: 0 }}>
        <div style={{ height: '100%', width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            slots={{
              toolbar: EditToolbar,
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25, page: 0 },
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
          />
        </div>
      </Box>
      <ViewStatementDialog
        open={openViewDialog}
        documentToView={documentToView}
        onCancel={onCancelView}
      />
      <DocumentQualityDialog
        open={openDocumentQualityDialog}
        onClose={onCancelDocumentQualityDialog}
        selectedDocument={documentToView}
      />
      <CancelScanConfirmDialog
        open={openCancelConfirmDialog}
        onConfirm={handleConfirmCancel}
        onClose={handleCloseCancelConfirmDialog}
        document={documentToCancel}
      />
      <DocumentSummaryDialog
        open={openSummaryDialog}
        onClose={() => setOpenSummaryDialog(false)}
        rows={rows}
      />
      <ReissueConfirmDialog />
      <Snackbar
        open={reissueSuccess}
        autoHideDuration={6000}
        onClose={() => setReissueSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setReissueSuccess(false)}>
          Re-issue started — a fresh scan is processing for this document.
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(reissueError)}
        autoHideDuration={8000}
        onClose={() => setReissueError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setReissueError(null)}>
          {reissueError}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}